import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { ConfirmDialog, TextField } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import { PaymentLinkStatus } from "enums/PaymentLink";
import { PaymentLink, usePaymentLinkData } from "hooks/usePaymentLinkData";

interface DeactivatePaymentLinkModalProps {
  open: boolean;
  paymentLink: PaymentLink | null;
  onClose: () => void;
  onDone: () => void;
}

const DeactivatePaymentLinkModal: React.FC<DeactivatePaymentLinkModalProps> = ({
  open,
  paymentLink,
  onClose,
  onDone,
}) => {
  const { t } = useTranslation();
  const { deactivate, reactivate, loading } = usePaymentLinkData();
  const [inactiveMessage, setInactiveMessage] = useState("");

  const isActive = paymentLink?.status === PaymentLinkStatus.ACTIVE;

  const handleConfirm = async () => {
    if (!paymentLink) return;

    const succeeded = isActive
      ? await deactivate(paymentLink.id, inactiveMessage.trim() || undefined)
      : await reactivate(paymentLink.id);

    if (succeeded) {
      setInactiveMessage("");
      onDone();
      onClose();
    }
  };

  return (
    <ConfirmDialog
      open={open}
      tone={isActive ? "danger" : "primary"}
      title={
        isActive ? t("Deactivate payment link") : t("Reactivate payment link")
      }
      message={
        <Stack gap="var(--space-4)">
          <Typography variant="body2">
            {isActive
              ? t("paymentLink.DeactivateWarning")
              : t("paymentLink.ReactivateWarning")}
          </Typography>
          {isActive && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t("Message for visitors")}
              placeholder={t("paymentLink.InactiveMessagePlaceholder")}
              value={inactiveMessage}
              onChange={(event) => setInactiveMessage(event.target.value)}
            />
          )}
        </Stack>
      }
      confirmLabel={isActive ? t("Deactivate") : t("Reactivate")}
      cancelLabel={t("Cancel")}
      busy={loading}
      onConfirm={handleConfirm}
      onCancel={() => {
        if (loading) return;
        onClose();
      }}
    />
  );
};

export default DeactivatePaymentLinkModal;
